<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
                Budget Release List
              </v-expansion-panel-title>

              <v-expansion-panel-text>
                <v-card class="pa-4">
                  <v-data-table
                    :headers="headers"
                    :items="getBudgeReleasetList"
                    :item-value="id"
                    class="elevation-1"
                  >
                    <template #item.index="{ index }">{{ ++index }}</template>
                    <template #item.budget_type="{ item }">
                      {{ item?.budget_sub_head?.budget_head?.budget_type?.name || 'N/A' }}
                    </template>
                    <template #item.budget_head="{ item }">
                      {{ item?.budget_sub_head?.budget_head?.name || 'N/A' }}
                    </template>
                    <template #item.budget_sub_head="{ item }">
                      {{ item?.budget_sub_head?.name || 'N/A' }}
                    </template>
                    <template #item.budget_status="{ item }">
                      <v-chip v-if="item.budget_status === 'approved'" color="green" size="small">
                        Approved
                      </v-chip>
                      <v-chip v-else color="orange" size="small">Pending</v-chip>
                    </template>
                    <template #item.status="{ item }">
                      <v-chip v-if="item.status == 1" color="success" size="small">Active</v-chip>
                      <v-chip v-else color="grey" size="small">Draft</v-chip>
                    </template>
                    <template #item.actions="{ item }">
                      <v-icon small @click="viewDetails(item)" class="me-2 edit-button" title="View Budget" color="blue">
                        mdi-file-document-outline
                      </v-icon>
                      <v-icon v-if="canEdit(item)" class="me-2 edit-button" color="primary" @click="editItem(item)">
                        mdi mdi-pencil-outline
                      </v-icon>
                      <v-icon v-if="item.budget_status === 'approved'" class="me-2 edit-button" color="primary" @click="openReviseModal(item)" title="Revise Budget">
                        mdi mdi-reload
                      </v-icon>
                      <v-icon v-if="item.revisions && item.revisions.length > 0" class="me-2 edit-button" color="teal" @click="openRevisionListModal(item)" title="Revision History">
                        mdi mdi-history
                      </v-icon>
                    </template>
                  </v-data-table>
                </v-card>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>

      <!-- -----------------------------
      Revise Budget Modal
      ---------------------------------->
      <v-dialog v-model="showReviseModal" max-width="500px">
        <v-card class="pa-3">
          <v-card-title>Revise Budget</v-card-title>
          <v-card-text>
            <v-form >
              <v-text-field
                label="Amount *"
                v-model="amount"
                density="compact"
                variant="outlined"
                :error-messages="amountError"
                required
              />
              <v-textarea
                label="Remarks"
                v-model="remarks"
                density="compact"
                variant="outlined"
                :error-messages="remarksError"
                clearable
              />
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey" variant="flat" @click="closeReviseModal">Cancel</v-btn>
            <v-btn color="primary" variant="flat" @click="submitRevise" :loading="loading">Revise</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- -----------------------------
      Revision List Modal
      ---------------------------------->
      <v-dialog v-model="showRevisionListModal" max-width="700px">
        <v-card class="pa-4">
          <v-card-title>Revision History</v-card-title>
          <v-card-text>
            <v-data-table :headers="revisionHeaders" :items="selectedBudget?.revisions || []" class="elevation-1" dense>
              <template #item.index="{ index }">{{ ++index }}</template>
              <template #item.version="{ item }">{{ item.version }}</template>
              <template #item.amount="{ item }">{{ item.amount }}</template>
              <template #item.remarks="{ item }">{{ item.remarks || '-' }}</template>
            </v-data-table>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey" variant="flat" @click="showRevisionListModal = false">Close</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-app>
</template>

<script setup>
import { ref, computed } from "vue"
import { useStore } from "vuex"
import { useRouter } from "vue-router"
import { toast } from "vue3-toastify"
import * as yup from "yup"
import { useForm, useField } from "vee-validate"

const router = useRouter()
const store = useStore()

const panel = ref([0])
const showReviseModal = ref(false)
const showRevisionListModal = ref(false)
const loading = ref(false)

const selectedBudget = ref(null)

const schema = yup.object({
  amount: yup.number().required("Amount is required"),
  remarks: yup.string(),
})

const { handleSubmit, setErrors, resetForm } = useForm({
  validationSchema: schema,
  initialValues: {
    amount: 0,
    remarks: ""
  }
})

const { value: amount, errorMessage: amountError } = useField("amount");
const { value: remarks, errorMessage: remarksError } = useField("remarks");


const headers = [
  { title: "#SL", align: "center", key: "index" },
  { title: "Code", key: "code" },
  { title: "Department", key: "department.name" },
  { title: "Financial Year", key: "financial_year" },
  { title: "Approval Status", key: "approval_status" },
  { title: "Status", key: "status" },
  { title: "Description", key: "description" },
  { title: "Actions", key: "actions" },
]

const revisionHeaders = [
  { title: "#SL", key: "index" },
  { title: "Version", key: "version" },
  { title: "Amount", key: "amount" },
  { title: "Remarks", key: "remarks" },
]

const getBudgeReleasetList = computed(() => store.getters.getBudgeReleasetList)

const loadBudgeReleasetList = async () => {
  try {
    store.commit("SET_LOADING", true); // start loading

    await store.dispatch("getBudgeReleasetList");

  } catch (error) {
    console.error(error);
  } finally {
    store.commit("UNSET_LOADING"); // stop loading (always)
  }
};


function editItem(item) { router.push({ name: "budget-release-create", query: { id: item.id } }) }
function viewDetails(item) { router.push({ name: "budget-release-details", params: { id: item.id } }) }
function canEdit(item) { return item.approval_status === "pending" }


function openReviseModal(item) {
    selectedBudget.value = item
    resetForm({
        values: {
            amount: item.amount,
            remarks: ""
        }
    })
    showReviseModal.value = true
}

function closeReviseModal() {
    showReviseModal.value = false
    amount.value = 0
    remarks.value = ""
}

const submitRevise = handleSubmit(async () => {
  try {
    const payload = {
        amount: amount.value,
        remarks: remarks.value
    };

     loading.value = true
    try {
        await store.dispatch("reviseBudget", { id: selectedBudget.value.id, data: payload })
        toast.success("Budget revised successfully")
        closeReviseModal()
        loadBudgeReleasetList()
    } catch (error) {
        console.error(error)
        toast.error(error.response?.data?.message || "Failed to revise budget")
    } finally { loading.value = false }

  } catch (error) {
    if (error.response && error.response.status === 422) {
      setErrors(error.response.data.errors)
    }
  }
})


function openRevisionListModal(item) {
  selectedBudget.value = item
  showRevisionListModal.value = true
}

// ---------------------
// Mount
// ---------------------
loadBudgeReleasetList()
</script>